import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { 
  Sparkles, 
  Play, 
  Frame, 
  Lightbulb, 
  Scale, 
  CheckCircle2, 
  Rocket,
  ChevronRight,
  GraduationCap
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import DecisionChainMini from "./DecisionChainMini";

// DEF Logo
const DEF_LOGO = "https://customer-assets.emergentagent.com/job_clarity-steps/artifacts/5k4ib0ip_DEF%20.webp";

const stepIcons = {
  0: Sparkles,
  1: Play,
  2: Frame,
  3: Lightbulb,
  4: Scale,
  5: CheckCircle2,
  6: Rocket,
};

const ProgressSidebar = ({ steps, currentStep, decisionData, onStepClick, coachMode, onToggleCoach }) => {
  const completedSteps = currentStep;

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-72 bg-white border-r border-border flex-col z-40">
        {/* Logo Header */}
        <div className="p-6 border-b border-border">
          <img 
            src={DEF_LOGO} 
            alt="Decision Education Foundation" 
            className="h-10 w-auto"
          />
        </div>

        {/* Coach Mode Toggle */}
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-primary" />
              <Label htmlFor="coach-mode" className="text-sm font-medium text-foreground cursor-pointer">
                Coach Mode
              </Label>
            </div>
            <Switch
              id="coach-mode"
              checked={coachMode}
              onCheckedChange={onToggleCoach}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {coachMode ? "Showing guidance & tips" : "Clean, minimal view"}
          </p>
        </div>

        {/* Progress Steps */}
        <nav className="flex-1 overflow-y-auto custom-scrollbar py-4 px-4">
          <div className="space-y-1">
            {steps.slice(1).map((step, index) => {
              const stepNumber = index + 1;
              const Icon = stepIcons[stepNumber];
              const isActive = currentStep === stepNumber;
              const isCompleted = currentStep > stepNumber;
              const isAccessible = stepNumber <= currentStep;

              return (
                <motion.button
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => isAccessible && onStepClick(stepNumber)}
                  disabled={!isAccessible}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200",
                    isActive && "bg-primary/10 text-primary shadow-sm",
                    isCompleted && !isActive && "text-foreground hover:bg-muted",
                    !isAccessible && "text-muted-foreground/50 cursor-not-allowed",
                    isAccessible && !isActive && "hover:bg-muted cursor-pointer"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                    isActive && "bg-primary text-primary-foreground",
                    isCompleted && !isActive && "bg-success/20 text-success",
                    !isActive && !isCompleted && "bg-muted text-muted-foreground"
                  )}>
                    {isCompleted && !isActive ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm font-medium truncate",
                      isActive && "text-primary",
                      !isActive && !isCompleted && "text-muted-foreground"
                    )}>
                      {step.name}
                    </p>
                  </div>
                  {isActive && (
                    <ChevronRight className="w-4 h-4 text-primary shrink-0" />
                  )}
                </motion.button>
              );
            })}
          </div>
        </nav>

        {/* Mini Decision Chain */}
        <div className="p-4 border-t border-border">
          <DecisionChainMini ratings={decisionData.ratings} />
        </div>
      </aside>

      {/* Mobile Top Progress Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <img 
              src={DEF_LOGO} 
              alt="Decision Education Foundation" 
              className="h-8 w-auto"
            />
            <span className="text-sm text-muted-foreground">
              Step {currentStep} of 6
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / 6) * 100}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgressSidebar;
